import { useState, useCallback } from 'react';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';
import apiClient from '@/services/api/apiClient';

export interface StageSuggestions {
  [agentKey: string]: string | string[];
}

// What prior stages each agent reads from
export const STAGE_CONNECTIONS: Record<number, { reads: number[]; note: string }> = {
  1:  { reads: [],                   note: 'Uses Vibe Prospecting intelligence' },
  2:  { reads: [1],                  note: 'Uses Stage 1 signals + maturity band' },
  3:  { reads: [1, 2],               note: 'Uses Stage 2 contact response to tailor pain framing' },
  4:  { reads: [2, 3],               note: 'Uses confirmed maturity + pain axis to calculate gap' },
  5:  { reads: [3, 4],               note: 'Uses confirmed pain + gap to frame stack reality' },
  6:  { reads: [2, 3, 4, 5],         note: 'Uses all Discovery stage data — full call prep' },
  7:  { reads: [5, 6],               note: 'Uses confirmed stack (S5) + validated pain (S6)' },
  8:  { reads: [5, 6, 7],            note: 'Uses confirmed stack + architecture direction' },
  9:  { reads: [4, 6, 8],            note: 'Uses maturity gap + call outcomes + coexistence friction' },
  10: { reads: [5, 6, 9],            note: 'Uses stack + call + data readiness assessment' },
  11: { reads: [3, 6, 9, 10],        note: 'Uses confirmed pain + call + data + automation plan' },
  12: { reads: [6, 7, 8, 11],        note: 'Uses first call outcomes + architecture + decision intel' },
  13: { reads: [4, 6, 10, 12],       note: 'Uses gap + first call + automation + Stage 12 agreements' },
  14: { reads: [2,3,4,5,6,7,8,9,10,11,12,13], note: 'Synthesises the complete 13-stage journey' },
};

const STAGE_AGENT_LABEL: Record<number, string> = {
  1:'First Contact Prep', 2:'Maturity Model Framing', 3:'Pain Framing',
  4:'Gap Awareness', 5:'MES Reality Check', 6:'Alignment Call #1 Prep',
  7:'Architecture Thinking', 8:'Coexistence Framing', 9:'Data Readiness',
  10:'Automation Sequencing', 11:'Decision Intelligence', 12:'Confirmation Call #2 Prep',
  13:'90-Day Future State', 14:'MQL Sales Brief',
};

// ── useStageAgent hook ────────────────────────────────────────────────────────
export function useStageAgent(journeyId: string, stage: number, savedGuidance?: any) {
  const [guidance, setGuidance]   = useState<StageSuggestions | null>(savedGuidance || null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [ran, setRan]             = useState(!!savedGuidance);

  const run = useCallback(async (formData: Record<string, any> = {}, userNote = '') => {
    if (!journeyId) return;
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post(
        `/api/abm-journeys/${journeyId}/stage-agent`,
        { stage, formData, userNote },
        { timeout: 60000 }
      );
      setGuidance(data.guidance);
      setRan(true);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [journeyId, stage]);

  return { guidance, loading, error, ran, run, setGuidance };
}

// ── AgentRunBar component ─────────────────────────────────────────────────────
interface AgentRunBarProps {
  stage: number;
  ran: boolean;
  loading: boolean;
  error: string;
  onRun: () => void;
  note: string;
  onNoteChange: (v: string) => void;
}

export function AgentRunBar({ stage, ran, loading, error, onRun, note, onNoteChange }: AgentRunBarProps) {
  const [showNote, setShowNote] = useState(false);
  const conn = STAGE_CONNECTIONS[stage];

  return (
    <div className={`rounded-xl border p-3 space-y-2 ${ran ? 'border-violet-200 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-950/10' : 'border-dashed border-border bg-muted/20'}`}>
      <div className="flex items-center gap-2">
        <Sparkles className={`h-4 w-4 shrink-0 ${ran ? 'text-violet-500' : 'text-muted-foreground'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${ran ? 'text-violet-700 dark:text-violet-400' : 'text-muted-foreground'}`}>
            AI Agent — {STAGE_AGENT_LABEL[stage]}
          </p>
          {conn.reads.length > 0 ? (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              <span className="text-violet-500 font-medium">Reads: </span>
              {conn.reads.map(s => `S${s}`).join(' → ')}
              {' · '}{conn.note}
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground">{conn.note}</p>
          )}
          {ran && <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">Suggestions shown below each field — click Accept to apply</p>}
        </div>
        <button onClick={() => setShowNote(v => !v)}
          className="text-[10px] text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted transition-colors">
          {showNote ? 'Hide note' : '+ Context'}
        </button>
        <button onClick={onRun} disabled={loading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 ${
            ran
              ? 'bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 hover:bg-violet-200 border border-violet-200 dark:border-violet-800'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}>
          {loading
            ? <><Loader2 className="h-3 w-3 animate-spin" />Thinking…</>
            : ran
            ? <><RefreshCw className="h-3 w-3" />Refresh</>
            : <><Sparkles className="h-3 w-3" />Run Agent</>}
        </button>
      </div>

      {showNote && (
        <textarea value={note} onChange={e => onNoteChange(e.target.value)} rows={2}
          placeholder="Add context — e.g. 'Contact mentioned evaluating SAP ME' or 'Budget decision in Q3'"
          className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400/30 resize-none" />
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
